import React, { useState, useEffect, useRef } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import breakpoints from "utils/breakpoints";
import styled from "styled-components";
import { IconButton } from "components/components/atoms";
import useBreakpoint from "utils/hooks/useBreakpoint";
import getEmbedUrl from "utils/getEmbedUrl";
import getVideoType from "utils/getVideoType";
import pauseVideos from "utils/pauseVideos";
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from "utils/videoTranscriptFileUrl";
import { getScrFromIframe } from "utils/getScrFromIframe";
import Slider from "react-slick";

const CustomArrowContainer = styled.div`
  &:before {
    display: none;
  }
  z-index: 2;
`;
const Iframe = styled.iframe`
  height: 17.5rem;

  @media ${breakpoints.md} {
    min-height: 31.5rem;
  }
  @media ${breakpoints.lg} {
    height: 25.125rem;
  }
  @media ${breakpoints.xl} {
    height: 32.6rem;
  }
`;
const Video = styled.video`
  height: 17.5rem;

  @media ${breakpoints.md} {
    min-height: 31.5rem;
  }
  @media ${breakpoints.lg} {
    height: 25.125rem;
  }
  @media ${breakpoints.xl} {
    height: 32.6rem;
  }
`;

const CustomNextArrow = (props, isMobile) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx("mr-5 md:mr-10", className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className={cx(
          "absolute right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex mr-5 w-10 h-10 md:w-16 md:h-16 md:mr-8"
        )}
        icon="arrow-right"
        idIcon={"MultiblockGallery"}
        color="white"
        circled
        shadowed
        hoverState
        buttonLabel="Next slide of Multiblock"
        small={isMobile}
        title={"Right arrow icon"}
      />
    </CustomArrowContainer>
  );
};
const CustomPrevArrow = (props, isMobile) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx("ml-5 md:ml-10", className)}
      style={{ ...style }}
      onClick={onClick}
    >
      <IconButton
        className="absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200
        flex ml-5 sm:w-10 sm:h-10 md:w-16 md:h-16 md:ml-8"
        icon="arrow-left"
        idIcon={"MultiblockGallery"}
        color="white"
        circled
        shadowed
        hoverState
        buttonLabel="Previous slide on Multiblock"
        small={isMobile}
        title={"Left arrow icon"}
      />
    </CustomArrowContainer>
  );
};

const MultiblockImageCarousel = ({
  className,
  carousel,
  onChange = () => {},
  rounded = false,
  counterRight = false,
  isContainerRounded = true,
  reset = false,
  onChangeReset = () => {},
  isRightAligned = false,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  const [position, setPosition] = useState(0);

  const len = carousel.length;

  useEffect(() => {
    if (reset) {
      setPosition(0);
      onChangeReset(false);
    } else {
      if (position < 0) {
        const dist = Math.abs(position > len) ? position - len : position;
        onChange((len - Math.abs(dist % len)) % len);
      } else {
        onChange(position % len);
      }
    }
  }, [position, len]);

  const sliderSettings = {
    className: "slider w-full h-full overflow-hidden",
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 100,
    prevArrow: <CustomPrevArrow isMobile={isMobile} />,
    nextArrow: <CustomNextArrow isMobile={isMobile} />,
    afterChange: (current) => {
      setPosition(current);
    },
  };

  return (
    <div
      className={cx(
        "w-full h-full overflow-auto relative flex flex-col items-center",
        className,
        {
          rounded: isContainerRounded,
        }
      )}
      rounded={rounded}
    >
      <Slider {...sliderSettings}>
        {carousel &&
          carousel.map((item, index) => {
            const hasMultiblockVideo = (item.video && item.video.href) || false;
            const itemVideoType = getVideoType(hasMultiblockVideo);
            const videoTranscript = hasMultiblockVideo
              ? item.transcriptFile
              : {};
            const videoDescription = hasMultiblockVideo
              ? item.transcriptText
              : "";
            const imgSrc =
              (isMobile ? item.image.srcMobile : item.image.srcDesktop) ||
              item.image.src;
            return (
              <div
                className={cx("w-auto justify-center text-center rounded z-0 ")}
                key={imgSrc}
              >
                {!hasMultiblockVideo && (
                  <img
                    src={imgSrc}
                    alt={item && item.image && item.image.alt}
                    className="object-cover md:object-contain rounded"
                  />
                )}
                {hasMultiblockVideo && itemVideoType === "internal" && (
                  <Video
                    src={item.video.href}
                    className="min-h-full min-w-full bg-black rounded text-center"
                    controls
                    disablepictureinpicture="true"
                    controlslist="nodownload"
                  />
                )}
                {hasMultiblockVideo && itemVideoType === "external" && (
                  <div
                    className={cx(
                      "videoIframe w-full h-full rounded bg-black",
                      {}
                    )}
                  >
                    <Iframe
                      className="rounded w-full"
                      src={getEmbedUrl(
                        hasMultiblockVideo ? item.video.href : item.src,
                        false,
                        true
                      )}
                      title={videoDescription}
                    />
                  </div>
                )}

                {videoDescription &&
                  videoTranscript &&
                  videoTranscript.link &&
                  videoTranscript.link.href && (
                    <div
                      className={cx("flex", {
                        "lg:ml-16": !isRightAligned,
                      })}
                    >
                      <span
                        className={cx(
                          "richtext text-left text-md leading-7 font-graphie mt-2",
                          {
                            "lg:ml-4 text-mattamy-blue lg:text-white": !isRightAligned,
                            "text-white": isRightAligned,
                          }
                        )}
                      >
                        {"Transcript: "}
                        {
                          <a
                            href={videoTranscriptFileUrl(videoTranscript)}
                            download={
                              videoTranscript.name +
                              videoTranscriptFileExtension(
                                videoTranscript.extension
                              )
                            }
                            className="underline"
                            aria-label={
                              "Download " + videoDescription + " transcript"
                            }
                          >
                            {videoDescription}
                          </a>
                        }
                      </span>
                    </div>
                  )}
              </div>
            );
          })}
      </Slider>
      <div
        className={cx(
          "absolute bottom-0 rounded-full text-sm px-3 flex justify-center items-center font-trade-gothic-20 bg-white text-mattamy-blue mb-16 md:mb-32 lg:mb-8",
          {
            "right-0 mr-5": counterRight,
          }
        )}
      >
        <p className="mt-1">
          {Math.sign(position) !== -1
            ? (position % len) + 1
            : ((len - Math.abs(position % len)) % len) + 1}
          /{len}
        </p>
      </div>
      <span className="hidden" aria-live="polite">
        slide change. current slide is{" "}
        {Math.sign(position) !== -1
          ? (position % len) + 1
          : ((len - Math.abs(position % len)) % len) + 1}{" "}
        of {len}
      </span>
    </div>
  );
};

MultiblockImageCarousel.propTypes = {
  carousel: PropTypes.array.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func,
};

export default MultiblockImageCarousel;
